import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loginUser } from '../../services/authService';
import { getDeviceInfo } from '../../services/deviceFingerprint';
import { useAuth } from '../../context/AuthContext';
import { Form, Input, Button, Typography, Card } from 'antd';
import { message } from "antd";

const { Title, Text } = Typography;

const Login = () => {
  const navigate = useNavigate();
  const [formState, setFormState] = useState({ identifier: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const [messageApi, contextHolder] = message.useMessage(); //Need this as well to show message

  // Set by the API interceptor when a live session was cut because the account
  // was deactivated. Shown once, then cleared so it does not haunt later visits.
  useEffect(() => {
    const reason = sessionStorage.getItem('signedOutReason');
    if (reason) {
      sessionStorage.removeItem('signedOutReason');
      setError(reason);
      messageApi.error(reason, 8);
    }
  }, [messageApi]);

  const handleChange = (e) => {
    setFormState({ ...formState, [e.target.name]: e.target.value });
  };

  const handleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      // Attach a device fingerprint so the Login Policy Engine can enforce
      // device/IP restrictions. Never blocks login if it can't be computed.
      const device = await getDeviceInfo();
      const {
        user, token, message: loginMessage, mustChangePassword, passwordChangeReason, passwordExpiry,
      } = await loginUser({ ...formState, ...device });

      if (loginMessage === 'User not found') {
        messageApi.error(loginMessage);
      } else if (!token) {
        // A login policy denied the sign-in, or the account is deactivated —
        // the backend explains why. Held longer than a default toast: these
        // messages are a full sentence and tell the user what to do next.
        messageApi.error(loginMessage || 'Login failed', 8);
        setError(loginMessage || 'Login failed');
      } else if (mustChangePassword) {
        // PASSWORD_EXPIRY policy: signed in, but every other endpoint is
        // refused until the password is changed.
        login({ ...user, token });
        if (passwordChangeReason) sessionStorage.setItem('passwordChangeReason', passwordChangeReason);
        navigate('/change-password', { replace: true });
      } else {
        messageApi.success(loginMessage || 'Login successful');
        // Nudge ahead of expiry so nobody is caught out mid-shift.
        if (passwordExpiry?.warn) {
          messageApi.warning(
            `Your password expires in ${passwordExpiry.daysRemaining} day(s). Change it from your profile.`,
            6
          );
        }
        login({ ...user, token });
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('login error: ', err);
      const errorMessage = err.response?.data?.message || 'Login failed. Please check your credentials and try again.';
      messageApi.error(errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {contextHolder}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 100 }}>
        <Card style={{ width: 400 }}>
          <Title level={3} style={{ textAlign: 'center' }}>Login</Title>
          <Form layout="vertical" onFinish={handleLogin}>
            <Form.Item label="Employee ID" name="identifier" rules={[{ required: true, message: 'Please enter your Employee ID or email' }]}>
              <Input name="identifier" onChange={handleChange} placeholder="e.g. AM00001" />
            </Form.Item>
            <Form.Item label="Password" name="password" rules={[{ required: true, message: 'Please enter your password' }]}>
              <Input.Password name="password" onChange={handleChange} />
            </Form.Item>
            {error && <Text type="danger">{error}</Text>}
            <Form.Item>
              <Button type="primary" htmlType="submit" block loading={loading}>
                Login
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    </>
  );
};

export default Login;
